import { useAtom } from "jotai";
import { useContext, useEffect, useState } from "react";

import ResultList from "@/components/shared/venueAndTaskAndUserSearch/resultList";
import WordInput from "@/components/shared/venueAndTaskAndUserSearch/wordInput";
import { useSearchResultSelect } from "@/hooks/common/search/useSearchResultSelect";
import { useVenueAndTaskAndUserSearch } from "@/hooks/common/search/useVenueAndTaskAndUserSearch";
import { selectedSearchResultAtom } from "@/states/common/venueAndTaskAndUserSearch/selectedSearchResultAtom";
import { selectedSearchResultContractAtom } from "@/states/common/venueAndTaskAndUserSearch/selectedSearchResultContractAtom";
import { ApplyFilter } from "@/types/owner/task/applyFilter";
import { AuthContext } from "contexts/auth";

type Props = {
  user: "owner"; //MEMO: クルー非対応
  searchType: "venue" | "contract";
  filterList: any;
  showList: boolean;
  setShowList: React.Dispatch<React.SetStateAction<boolean>>;
  searched: boolean;
  setSearched: React.Dispatch<React.SetStateAction<boolean>>;
  setFilterList?: React.Dispatch<React.SetStateAction<any>>;
  setApplyFilter?: React.Dispatch<React.SetStateAction<ApplyFilter>>;
};
export type VenueAndTaskAndUserResponse = {
  id: number;
  name: string;
  task?: boolean;
  isUser?: boolean;
};

/* 物件と仕事をフリーワードから検索 */
const VenueAndTaskAndUserSearch: React.FC<Props> = ({
  user,
  searchType,
  filterList,
  setApplyFilter,
  showList,
  setShowList,
  searched,
  setSearched,
  setFilterList
}) => {
  const { token } = useContext(AuthContext);
  const [searchWord, setSearchWord] = useAtom(selectedSearchResultAtom); //フィルタと整合性を保つ為、検索ワードを保持
  const [contractSearchWord, setContractSearchWord] = useAtom(
    selectedSearchResultContractAtom
  ); //フィルタと整合性を保つ為、検索ワードを保持
  const [word, setWord] = useState("");
  const [searchVenues, setSearchVenues] = useState<
    VenueAndTaskAndUserResponse[]
  >([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [typing, setTyping] = useState(false);
  const [keyDownEnter, setKeyDownEnter] = useState(false);
  const [firstSearch, setFirstSearch] = useState(false);
  const [resultSelected, setResultSelected] = useState(false);
  const [activeType, setActiveType] = useState("");
  const [isSearched, setIsSearched] = useState(false);

  //MEMO: filterListがややこしい。jotaiにしてリセットを容易にした方が良さそう

  //検索結果を初期化
  const clearSerachResult = () => {
    if (word || isSearched) {
      if (setFilterList) {
        setFilterList({
          ...filterList,
          page: 1,
          venueId: [],
          cat_search: 0,
          crew_search: 0,
          q: "",
          equal: 0
        });
      }
      setShowList(false);
      setSearched(false);
      setWord("");
      if (searchType === "venue") {
        setSearchWord({
          word: "",
          activeType: ""
        });
      } else {
        setContractSearchWord({
          word: "",
          activeType: ""
        });
      }
      if (setApplyFilter) setApplyFilter("normal");
    }
  };

  //検索結果から物件or仕事を選択
  const selectVenue = (vt: VenueAndTaskAndUserResponse) => {
    if (setFilterList) {
      if (vt.isUser) {
        //ユーザーの場合
        setFilterList({
          ...filterList,
          page: 1,
          venueId: [],
          cat_search: 0,
          crew_search: 1,
          q: vt.name,
          equal: 0
        });
      } else if (!vt.task) {
        //物件の場合
        setFilterList({
          ...filterList,
          page: 1,
          pp: filterList.pp,
          venueId: [vt.id],
          cat_search: 0,
          q: "",
          equal: 0
        });
      } else {
        //仕事の場合
        setFilterList({
          ...filterList,
          page: 1,
          pp: filterList.pp,
          venueId: null,
          cat_search: 1,
          q: vt.name,
          equal: 1
        });
      }
    }
    setShowList(false);
    setSearched(true);
    setResultSelected(true);
    setWord(vt.name);
    setActiveType(vt.isUser ? "user" : vt.task ? "task" : "venue");
    const searchWordArr = {
      word: vt.name,
      activeType: vt.isUser ? "user" : vt.task ? "task" : "venue"
    };
    if (searchType === "venue") {
      setSearchWord(searchWordArr);
    } else {
      setContractSearchWord(searchWordArr);
    }
    if (setApplyFilter) setApplyFilter("normal");
  };

  //〇〇の仕事をすべて見るクリック時
  const selectAllTask = () => {
    if (setFilterList) {
      setFilterList({
        ...filterList,
        page: 1,
        pp: filterList.pp,
        venueId: [],
        cat_search: 1,
        q: word,
        equal: 0
      });
      setActiveType("task");
      setShowList(false);
      setSearched(true);
      if (setApplyFilter) setApplyFilter("normal");
      const searchWordArr = {
        word: word,
        activeType: "task"
      };
      if (searchType === "venue") {
        setSearchWord(searchWordArr);
      } else {
        setContractSearchWord(searchWordArr);
      }
    }
  };

  // 〇〇の依頼者/クルーをすべて見るクリック時
  const selectAllUserTask = () => {
    if (setFilterList) {
      setFilterList({
        ...filterList,
        page: 1,
        venueId: [],
        cat_search: 0,
        crew_search: 1,
        q: word,
        equal: 0
      });
    }
    setActiveType("user");
    setShowList(false);
    setSearched(true);
    if (setApplyFilter) setApplyFilter("normal");
    const searchWordArr = {
      word: word,
      activeType: "user"
    };
    if (searchType === "venue") {
      setSearchWord(searchWordArr);
    } else {
      setContractSearchWord(searchWordArr);
    }
  };

  //検索ワードから取得した物件情報をセット
  useSearchResultSelect({
    keyDownEnter: keyDownEnter,
    searchType: searchType,
    word: word,
    filterList: filterList,
    setShowList: setShowList,
    setSearched: setSearched,
    setKeyDownEnter: setKeyDownEnter,
    setSearchWord: setSearchWord,
    setContractSearchWord: setContractSearchWord,
    setFilterList: setFilterList,
    setApplyFilter: setApplyFilter
  });

  //検索ワードが変更されたら検索実行
  useVenueAndTaskAndUserSearch({
    word: word,
    typing: typing,
    firstSearch: firstSearch,
    resultSelected: resultSelected,
    searchType: searchType,
    user: user,
    token: token,
    searched: searched,
    filterList: filterList,
    setSearchLoading: setSearchLoading,
    setSearchVenues: setSearchVenues,
    setShowList: setShowList,
    setResultSelected: setResultSelected,
    setSearched: setSearched,
    setSearchWord: setSearchWord,
    setContractSearchWord: setContractSearchWord,
    setFilterList: setFilterList,
    setApplyFilter: setApplyFilter
  });

  //検索実行済か判定
  useEffect(() => {
    setIsSearched(
      (word ? true : false || filterList.venueId?.length > 0 || searched) &&
        searchType === "venue"
    );
  }, [word, filterList, searched, searchType]);

  //検索ワード/結果識別アイコンを保持（復元）
  useEffect(() => {
    if (searchType === "venue") {
      setWord(searchWord.word);
      if (searchWord.activeType) {
        setActiveType(
          searchWord.activeType === "venue"
            ? "venue"
            : searchWord.activeType === "user"
              ? "user"
              : "task"
        );
      }
    } else if (searchType === "contract") {
      setWord(contractSearchWord.word);
      if (contractSearchWord.activeType) {
        setActiveType(
          contractSearchWord.activeType === "venue"
            ? "venue"
            : contractSearchWord.activeType === "user"
              ? "user"
              : "task"
        );
      }
    }
  }, [searchType]);

  return (
    <>
      {/* 検索ワード入力 */}
      <div className="relative">
        <WordInput
          word={word}
          typing={typing}
          activeType={activeType}
          searchType={searchType}
          setWord={setWord}
          isSearched={isSearched}
          setFirstSearch={setFirstSearch}
          setKeyDownEnter={setKeyDownEnter}
          setTyping={setTyping}
          setActiveType={setActiveType}
          clearSerachResult={clearSerachResult}
        />
        {/* 検索結果表示 */}
        {word && showList && (
          <ResultList
            word={word}
            searchVenues={searchVenues}
            searchLoading={searchLoading}
            selectVenue={selectVenue}
            selectAllTask={selectAllTask}
            selectAllUserTask={selectAllUserTask}
          />
        )}
      </div>
    </>
  );
};

export default VenueAndTaskAndUserSearch;
